<div class="chat-list" [class.editable]="editable()">

  <!-- Empty state in editable mode: prompt to start the conversation -->
  @if (editable() && messages().length === 0) {
    <div class="chat-empty">
      <i class="pi pi-comments chat-empty-icon"></i>
      <h3 i18n="@@chatHistory.empty.title">No conversation context</h3>
      <p class="chat-empty-hint" i18n="@@chatHistory.empty.hint">
        Add messages to seed the simulated conversation. Previous messages will be passed to the agent as context.
      </p>
      <p i18n="@@chatHistory.empty.startsWith">Start with</p>
      <div class="chat-empty-actions">
        <p-button
          icon="pi pi-microchip"
          i18n-label="@@chatHistory.add.assistant"
          label="Agent message"
          severity="secondary"
          [outlined]="true"
          (click)="startWith('Assistant')">
        </p-button>
        <p-button
          icon="pi pi-user"
          i18n-label="@@chatHistory.add.user"
          label="User message"
          severity="secondary"
          [outlined]="true"
          (click)="startWith('User')">
        </p-button>
      </div>
    </div>
  }

  @for (row of rows(); track row.msg.id ?? $index) {
    @let msg = row.msg;
    @let isUser = msg.role === 'User';
    @let isAssistant = msg.role === 'Assistant';
    @let isSystem = msg.role === 'System';
    @let kind = msg.kind ?? 'Message';

    <!-- AgentHandoff: centered system-style notice (history-only) -->
    @if (kind === 'AgentHandoff') {
      <div class="msg-row is-system handoff-row">
        <div class="system-notice">
          <i class="pi pi-arrow-right-arrow-left"></i>
          <span i18n="@@chatHistory.kind.handoff">Agent handoff</span>
          @if (msg.handoff_to_agent_id) {
            <span class="mono">{{ msg.handoff_to_agent_id }}</span>
          }
        </div>
      </div>
    } @else {
      <div
        class="msg-row"
        [class.is-user]="isUser"
        [class.is-agent]="isAssistant"
        [class.is-system]="isSystem"
        [class.clickable]="clickable() && !editable()"
        (click)="onRowClick(msg)">

        @if (isAssistant) {
          <div class="agent-header">
            <i class="pi pi-microchip"></i>
            @if (row.assistantLink; as link) {
              <a [routerLink]="link" class="agent-name-link" (click)="$event.stopPropagation()">{{ row.assistantHeader }}</a>
            } @else {
              <span>{{ row.assistantHeader }}</span>
            }
            @if (kind !== 'Message') {
              <p-tag [value]="kindFallbackLabel(kind)" severity="info" class="kind-tag"></p-tag>
            }
          </div>
        }

        <!-- Body: editable textarea OR read-only bubble / tool card -->
        @if (editable() && kind === 'Message') {
          <!-- НЕ используем [autoResize]="true" — PrimeNG-директива
               гоняет resize() из onAfterViewChecked на КАЖДОМ Angular
               change-detection'е (height:auto→height:scrollHeight),
               chat-pane трясётся при любом клике, а PrimeNG p-menu
               popup ловит scroll на trigger'е и закрывается. Вместо
               этого — CSS field-sizing: content: браузер сам растит
               textarea по контенту, без JS и без CD-зависимости. -->
          <textarea
            pTextarea
            rows="1"
            class="msg-bubble msg-bubble-autosize"
            [class.bubble-user]="isUser"
            [class.bubble-agent]="isAssistant"
            i18n-placeholder="@@chatHistory.message.placeholder"
            placeholder="Message"
            [ngModel]="msg.content"
            (ngModelChange)="updateContent($index, $event)">
          </textarea>
        } @else {
          @switch (kind) {
            @case ('ToolCall') {
              <div class="tool-card">
                <div class="tool-card-header">
                  <i class="pi pi-wrench"></i>
                  <span class="tool-name">{{ msg.tool_name || '-' }}</span>
                  @if (msg.tool_call_id) {
                    <span class="mono tool-call-id">{{ msg.tool_call_id }}</span>
                  }
                </div>
                @if (row.argsPretty) {
                  <pre class="tool-args mono">{{ row.argsPretty }}</pre>
                }
              </div>
            }
            @case ('ToolCallResult') {
              <div class="tool-card" [class.is-error]="msg.tool_is_error">
                <div class="tool-card-header">
                  <i [class]="msg.tool_is_error ? 'pi pi-times-circle' : 'pi pi-check-circle'"></i>
                  <span class="tool-name">
                    <ng-container i18n="@@chatHistory.kind.toolResult">Tool result</ng-container>
                    @if (msg.tool_name) { · {{ msg.tool_name }} }
                  </span>
                  @if (msg.tool_is_error) {
                    <p-tag severity="danger" i18n-value="@@chatHistory.tool.error" value="Error" class="kind-tag"></p-tag>
                  }
                </div>
                @if (msg.tool_output) {
                  <pre class="tool-args mono">{{ msg.tool_output }}</pre>
                }
              </div>
            }
            @default {
              <!-- Bubble + inline «Создать тест отсюда» рядом. Flex-row,
                   у user'а разворачиваем через row-reverse (см. CSS) —
                   баббл справа, кнопка слева от него. У ассистента —
                   баббл слева, кнопка справа. -->
              <div class="bubble-with-action">
                <div
                  class="msg-bubble"
                  [class.bubble-user]="isUser"
                  [class.bubble-agent]="isAssistant"
                  [class.bubble-system]="isSystem"
                  [class.is-streaming]="row.streaming">
                  {{ msg.content || '' }}
                  @if (row.streaming) {
                    <i class="pi pi-spin pi-spinner streaming-indicator"></i>
                  }
                </div>
                @if (!editable() && testFromHere() && !isSystem) {
                  <div class="test-from-here-actions" (click)="$event.stopPropagation()">
                    <p-button
                      icon="pi pi-flask"
                      [text]="true"
                      severity="secondary"
                      size="small"
                      i18n-pTooltip="@@chatHistory.testFromHere"
                      pTooltip="Create test from here"
                      [tooltipPosition]="isUser ? 'right' : 'left'"
                      (click)="createTestFrom.emit(msg)"
                      i18n-ariaLabel="@@chatHistory.testFromHere"
                      ariaLabel="Create test from here">
                    </p-button>
                  </div>
                }
              </div>
            }
          }
        }

        <!-- Editable mode: per-row trash + "add after" actions -->
        @if (editable() && kind === 'Message') {
          <div class="msg-actions" (click)="$event.stopPropagation()">
            <p-button
              icon="pi pi-trash"
              [text]="true"
              severity="danger"
              size="small"
              i18n-pTooltip="@@chatHistory.remove"
              pTooltip="Remove message"
              (click)="remove($index)"
              i18n-ariaLabel="@@chatHistory.remove"
              ariaLabel="Remove message">
            </p-button>
            <!-- mousedown.preventDefault: без него браузер фокусирует
                 кнопку → скроллит .chat-pane (overflow:auto) чтобы
                 фокус был виден → PrimeNG p-menu (popup) видит scroll
                 родителя trigger'а через ConnectedOverlayScrollHandler
                 и закрывает меню сразу после открытия. Мы фокус на
                 кнопке не используем — a11y не страдает. -->
            <p-button
              icon="pi pi-plus"
              [text]="true"
              severity="secondary"
              size="small"
              i18n-pTooltip="@@chatHistory.addAfter"
              pTooltip="Add message after"
              (mousedown)="$event.preventDefault()"
              (click)="openAddMenuAfter($index, $event)"
              i18n-ariaLabel="@@chatHistory.addAfter"
              ariaLabel="Add message after">
            </p-button>
          </div>
        }

        <!-- Footer (read-only metadata): timestamp + interrupted + metrics -->
        @if (!editable()) {
          <div class="msg-meta">
            @if (showTimestamps() && msg.created_at) {
              <span class="msg-time">{{ msg.created_at | date:'HH:mm:ss' }}</span>
            }
            @if (msg.interrupted) {
              <p-tag severity="warn" i18n-value="@@chatHistory.interrupted" value="Interrupted" class="kind-tag"></p-tag>
            }
            @if (row.metrics.length > 0) {
              <span class="metrics">
                @for (m of row.metrics; track m.tooltip) {
                  <span class="metric" [pTooltip]="m.tooltip" tooltipPosition="top">
                    <i [class]="m.icon"></i>
                    {{ m.value }}
                  </span>
                }
              </span>
            }
            <!-- SKN-363: engine badge (LLM/Realtime) под метриками в том же
                 стиле (тот же .metric класс — шрифт/background/padding).
                 Обёртка .msg-engine занимает всю ширину внутри .msg-meta
                 (flex-basis:100%), из-за чего badge переносится на новую
                 строку под timestamp/interrupted/metrics. Tooltip раскрывает
                 STT/TTS/VAD для classic-pipeline (у realtime — пусто). -->
            @if (row.engineBadge) {
              <div class="msg-engine">
                <span class="metric engine-metric"
                      [pTooltip]="row.engineTooltip"
                      [tooltipDisabled]="!row.engineTooltip"
                      tooltipPosition="top">
                  <i class="pi pi-microchip"></i>
                  {{ row.engineBadge }}
                </span>
              </div>
            }
          </div>
        }
      </div>
    }
  }

  @if (!editable() && rows().length === 0) {
    <div class="chat-empty-row">
      <i class="pi pi-comments empty-icon"></i>
      <p i18n="@@chatHistory.empty">No messages</p>
    </div>
  }

  <!-- Shared popup menu for `+` buttons in editable mode -->
  @if (editable()) {
    <p-menu #addMenu [popup]="true" [model]="addMenuItems" appendTo="body"></p-menu>
  }
</div>
